import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, enforceVisibilityInvariant, sha256Schema, tenantOwnedShape, visibilityShape, } from "./common.js";
import { documentBlockIdSchema, documentIdSchema, documentVersionIdSchema, } from "./ids.js";
import { documentVersionRefSchema, sourceItemRefSchema } from "./refs.js";
import { completenessStateSchema } from "./vocabulary.js";
export const documentSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: documentIdSchema,
    sourceItemRef: sourceItemRefSchema,
    currentVersionRef: documentVersionRefSchema.nullable(),
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        { tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] },
        ...(value.currentVersionRef
            ? [
                {
                    tenantId: value.currentVersionRef.tenantId,
                    path: ["currentVersionRef"],
                },
            ]
            : []),
    ], context);
});
export const documentVersionSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: documentVersionIdSchema,
    documentId: documentIdSchema,
    sourceItemRef: sourceItemRefSchema,
    version: z.number().int().positive(),
    sourceVersion: z.string().min(1).nullable(),
    sourceSha256: sha256Schema,
    canonicalSha256: sha256Schema,
    canonicalByteLength: z.number().int().nonnegative(),
    blockCount: z.number().int().nonnegative(),
    parserVersion: z.string().min(1),
    completenessState: completenessStateSchema,
    analysisDerivativeSha256: sha256Schema.nullable(),
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [{ tenantId: value.sourceItemRef.tenantId, path: ["sourceItemRef"] }], context);
    if (value.completenessState !== "success") {
        context.addIssue({
            code: "custom",
            path: ["completenessState"],
            message: "DocumentVersion is canonical only after a successful completeness check.",
        });
    }
});
export const documentBlockSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    id: documentBlockIdSchema,
    documentVersionRef: documentVersionRefSchema,
    orderIndex: z.number().int().nonnegative(),
    byteStart: z.number().int().nonnegative(),
    byteEndExclusive: z.number().int().nonnegative(),
    blockKind: z.enum([
        "heading",
        "paragraph",
        "table",
        "list",
        "email_header",
        "email_body",
        "attachment",
        "structural",
    ]),
    content: z.string(),
    sourceLocator: z
        .object({
        page: z.number().int().positive().nullable(),
        section: z.string().max(1024).nullable(),
        nativeSegmentIndex: z.number().int().nonnegative().nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
    enforceSameTenant(value.tenantId, [
        {
            tenantId: value.documentVersionRef.tenantId,
            path: ["documentVersionRef"],
        },
    ], context);
    if (value.byteEndExclusive < value.byteStart) {
        context.addIssue({
            code: "custom",
            path: ["byteEndExclusive"],
            message: "Block byte range is inverted.",
        });
    }
});
export class DocumentIntegrityError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "DocumentIntegrityError";
    }
}
export function reconstructOrderedBlocks(blocksInput) {
    const blocks = blocksInput.map((block) => documentBlockSchema.parse(block));
    if (blocks.length === 0) {
        return { canonicalMarkdown: "", canonicalBytes: new Uint8Array() };
    }
    const first = blocks[0];
    let expectedByteStart = 0;
    let markdown = "";
    for (const [index, block] of blocks.entries()) {
        if (block.tenantId !== first.tenantId) {
            throw new DocumentIntegrityError("cross_tenant", "Document blocks cross a tenant boundary.");
        }
        if (block.documentVersionRef.documentVersionId !==
            first.documentVersionRef.documentVersionId) {
            throw new DocumentIntegrityError("wrong_version", "Document blocks belong to different versions.");
        }
        if (block.orderIndex !== index) {
            throw new DocumentIntegrityError("block_order", `Expected block order ${index}; received ${block.orderIndex}.`);
        }
        const bytes = new TextEncoder().encode(block.content);
        if (block.byteStart !== expectedByteStart ||
            block.byteEndExclusive !== expectedByteStart + bytes.byteLength) {
            throw new DocumentIntegrityError("byte_range", `Block ${index} byte range does not match its UTF-8 content.`);
        }
        markdown += block.content;
        expectedByteStart += bytes.byteLength;
    }
    return {
        canonicalMarkdown: markdown,
        canonicalBytes: new TextEncoder().encode(markdown),
    };
}
export async function sha256Hex(value) {
    const bytes = typeof value === "string" ? new TextEncoder().encode(value) : value;
    const copy = new Uint8Array(bytes);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", copy);
    const hex = [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
    return sha256Schema.parse(hex);
}
export async function verifyDocumentReconstruction(versionInput, blocks) {
    const version = documentVersionSchema.parse(versionInput);
    if (blocks.length !== version.blockCount) {
        throw new DocumentIntegrityError("block_count", `Expected ${version.blockCount} blocks; received ${blocks.length}.`);
    }
    const reconstructed = reconstructOrderedBlocks(blocks);
    if (reconstructed.canonicalBytes.byteLength !== version.canonicalByteLength) {
        throw new DocumentIntegrityError("byte_length", "Reconstructed UTF-8 byte length does not match DocumentVersion.");
    }
    const hash = await sha256Hex(reconstructed.canonicalBytes);
    if (hash !== version.canonicalSha256) {
        throw new DocumentIntegrityError("hash_mismatch", "Reconstructed canonical Markdown hash does not match DocumentVersion.");
    }
    return reconstructed;
}
//# sourceMappingURL=document.js.map